import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faPlus, faTrash, faEye, faEyeSlash, faCopy, faCheck, faBook } from '@fortawesome/free-solid-svg-icons';

interface ApiKey {
    id: number;
    label: string;
    apiKey: string;
    created: string;
    active: boolean;
}

interface GenericResponse {
    message: string;
    status: boolean;
}

interface UserInfo {
    userName: string;
    email: string;
    type: string;
}

const ApiKeys: React.FC = () => {
    const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [creating, setCreating] = useState<boolean>(false);
    const [showCreateForm, setShowCreateForm] = useState<boolean>(false);
    const [newKeyLabel, setNewKeyLabel] = useState<string>('');
    const [deactivatingId, setDeactivatingId] = useState<number | null>(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
    const [keyToDeactivate, setKeyToDeactivate] = useState<ApiKey | null>(null);
    const [visibleKeys, setVisibleKeys] = useState<Set<number>>(new Set());
    const [copiedKeyId, setCopiedKeyId] = useState<number | null>(null);
    const [copiedCurlId, setCopiedCurlId] = useState<string | null>(null);
    const [showDocs, setShowDocs] = useState<boolean>(false);
    const [isAdmin, setIsAdmin] = useState<boolean>(false);

    useEffect(() => {
        fetchApiKeys();
        fetchUserRole();
    }, []);

    const fetchUserRole = async () => {
        try {
            const response = await axios.get<UserInfo>('/pool/info');
            setIsAdmin(response.data.type === 'ADMIN');
        } catch (error) {
            console.error('Error fetching user role:', error);
            setIsAdmin(false);
        }
    };

    const fetchApiKeys = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/pool/apiKeys/list');
            setApiKeys(response.data);
        } catch (error) {
            console.error('Error fetching API keys:', error);
        } finally {
            setLoading(false);
        }
    };

    const generateRandomLabel = () => {
        const adjectives = ['Swift', 'Secure', 'Smart', 'Fast', 'Bold', 'Prime', 'Elite', 'Pro'];
        const nouns = ['Key', 'Access', 'Token', 'Gateway', 'Bridge', 'Portal', 'Link', 'Pass'];
        const randomNumber = Math.floor(Math.random() * 1000);

        const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)];
        const randomNoun = nouns[Math.floor(Math.random() * nouns.length)];

        return `${randomAdjective} ${randomNoun} ${randomNumber}`;
    };

    const handleCreateKey = async () => {
        if (!newKeyLabel.trim()) {
            setNewKeyLabel(generateRandomLabel());
            return;
        }

        try {
            setCreating(true);
            const response = await axios.post('/pool/apiKeys/new', {
                label: newKeyLabel
            });

            if (response.data.status) {
                await fetchApiKeys();
                setShowCreateForm(false);
                setNewKeyLabel('');
            }
        } catch (error) {
            console.error('Error creating API key:', error);
        } finally {
            setCreating(false);
        }
    };

    const handleDeactivateKey = (apiKey: ApiKey) => {
        setKeyToDeactivate(apiKey);
        setShowConfirmDialog(true);
    };

    const confirmDeactivation = async () => {
        if (!keyToDeactivate) return;

        try {
            setDeactivatingId(keyToDeactivate.id);
            const response = await axios.post('/pool/apiKeys/deActive', {
                id: keyToDeactivate.id,
                key: keyToDeactivate.apiKey
            });

            if (response.data.status) {
                await fetchApiKeys();
            }
        } catch (error) {
            console.error('Error deactivating API key:', error);
        } finally {
            setDeactivatingId(null);
            setShowConfirmDialog(false);
            setKeyToDeactivate(null);
        }
    };

    const toggleKeyVisibility = (keyId: number) => {
        const newVisibleKeys = new Set(visibleKeys);
        if (newVisibleKeys.has(keyId)) {
            newVisibleKeys.delete(keyId);
        } else {
            newVisibleKeys.add(keyId);
        }
        setVisibleKeys(newVisibleKeys);
    };

    const copyToClipboard = async (text: string, type: 'key' | 'curl', id: string | number) => {
        try {
            await navigator.clipboard.writeText(text);
            if (type === 'key') {
                setCopiedKeyId(id as number);
                setTimeout(() => setCopiedKeyId(null), 2000);
            } else {
                setCopiedCurlId(id as string);
                setTimeout(() => setCopiedCurlId(null), 2000);
            }
        } catch (error) {
            console.error('Failed to copy:', error);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);

            if (type === 'key') {
                setCopiedKeyId(id as number);
                setTimeout(() => setCopiedKeyId(null), 2000);
            } else {
                setCopiedCurlId(id as string);
                setTimeout(() => setCopiedCurlId(null), 2000);
            }
        }
    };

    const formatApiKey = (apiKey: string, isVisible: boolean) => {
        if (isVisible) {
            return apiKey;
        }
        return apiKey.substring(0, 8) + '••••••••' + apiKey.substring(apiKey.length - 4);
    };

    const activeKeys = apiKeys.filter(key => key.active);
    const hasActiveKeys = activeKeys.length > 0;
    const hasOnlyDeactivatedKeys = apiKeys.length > 0 && activeKeys.length === 0;

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <FontAwesomeIcon icon={faKey} className="text-blue-600 text-xl" />
                            <h2 className="text-xl font-bold text-gray-900">API Keys</h2>
                        </div>
                        <div className="flex gap-2">
                            {hasActiveKeys && (
                                <button
                                    onClick={() => setShowDocs(!showDocs)}
                                    className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200"
                                >
                                    <FontAwesomeIcon icon={faBook} />
                                    {showDocs ? 'Hide' : 'Show'} API Docs
                                </button>
                            )}
                            {(apiKeys.length === 0 || hasOnlyDeactivatedKeys) && (
                                <button
                                    onClick={() => setShowCreateForm(true)}
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                    Create API Key
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    {apiKeys.length === 0 ? (
                        <div className="text-center py-12">
                            <FontAwesomeIcon icon={faKey} className="text-gray-400 text-4xl mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No API Keys Found</h3>
                            <p className="text-gray-500 mb-6">Create your first API key to get started with programmatic access.</p>
                        </div>
                    ) : hasOnlyDeactivatedKeys ? (
                        <div className="text-center py-12">
                            <FontAwesomeIcon icon={faKey} className="text-gray-400 text-4xl mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">All API Keys Deactivated</h3>
                            <p className="text-gray-500 mb-6">All your API keys have been deactivated. Create a new one to continue.</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {activeKeys.map((apiKey) => (
                                <div key={apiKey.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <h4 className="font-semibold text-gray-900">{apiKey.label}</h4>
                                                <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                                    Active
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2 mb-2">
                                                <code className="bg-white px-3 py-2 rounded border text-sm font-mono flex-1">
                                                    {formatApiKey(apiKey.apiKey, visibleKeys.has(apiKey.id))}
                                                </code>
                                                <button
                                                    onClick={() => toggleKeyVisibility(apiKey.id)}
                                                    className="p-2 text-gray-500 hover:text-gray-700 transition-colors duration-200"
                                                    title={visibleKeys.has(apiKey.id) ? "Hide API key" : "Show API key"}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={visibleKeys.has(apiKey.id) ? faEyeSlash : faEye}
                                                        className="w-4 h-4"
                                                    />
                                                </button>
                                                <button
                                                    onClick={() => copyToClipboard(apiKey.apiKey, 'key', apiKey.id)}
                                                    className="p-2 text-gray-500 hover:text-gray-700 transition-colors duration-200"
                                                    title="Copy API key"
                                                >
                                                    <FontAwesomeIcon
                                                        icon={copiedKeyId === apiKey.id ? faCheck : faCopy}
                                                        className={`w-4 h-4 ${copiedKeyId === apiKey.id ? 'text-green-500' : ''}`}
                                                    />
                                                </button>
                                            </div>
                                            <p className="text-sm text-gray-500">
                                                Created: {new Date(apiKey.created).toLocaleDateString()}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => handleDeactivateKey(apiKey)}
                                            disabled={deactivatingId === apiKey.id}
                                            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {deactivatingId === apiKey.id ? (
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            ) : (
                                                <FontAwesomeIcon icon={faTrash} />
                                            )}
                                            Deactivate
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Create Key Form */}
                    {showCreateForm && (
                        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <h4 className="font-semibold text-gray-900 mb-4">Create New API Key</h4>
                            <div className="space-y-4">
                                <div>
                                    <label htmlFor="label" className="block text-sm font-medium text-gray-700 mb-1">
                                        Label (optional)
                                    </label>
                                    <input
                                        type="text"
                                        id="label"
                                        value={newKeyLabel}
                                        onChange={(e) => setNewKeyLabel(e.target.value)}
                                        placeholder="Enter a label or leave empty for auto-generation"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleCreateKey}
                                        disabled={creating}
                                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {creating ? (
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        ) : (
                                            <FontAwesomeIcon icon={faPlus} />
                                        )}
                                        Create Key
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowCreateForm(false);
                                            setNewKeyLabel('');
                                        }}
                                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors duration-200"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* API Documentation */}
                    {showDocs && hasActiveKeys && (
                        <div className="mt-6 bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                            <div className="bg-gray-100 px-6 py-4 border-b border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <FontAwesomeIcon icon={faBook} />
                                    API Documentation
                                </h3>
                                <p className="text-sm text-gray-600 mt-1">
                                    Use these examples to integrate with our API endpoints
                                </p>
                            </div>

                            <div className="p-6 space-y-6">
                                {/* Authentication */}
                                <div>
                                    <h4 className="text-md font-semibold text-gray-800 mb-3">Authentication</h4>
                                    <p className="text-sm text-gray-600 mb-3">
                                        Include your API key in the Authorization header as a Bearer token:
                                    </p>
                                    <div className="bg-gray-800 rounded-lg p-4 relative">
                                        <button
                                            onClick={() => copyToClipboard(`Authorization: Bearer YOUR_API_KEY`, 'curl', 'auth-header')}
                                            className="absolute top-2 right-2 p-2 text-gray-400 hover:text-white transition-colors duration-200"
                                            title="Copy header"
                                        >
                                            <FontAwesomeIcon
                                                icon={copiedCurlId === 'auth-header' ? faCheck : faCopy}
                                                className={`w-4 h-4 ${copiedCurlId === 'auth-header' ? 'text-green-400' : ''}`}
                                            />
                                        </button>
                                        <code className="text-green-400 text-sm font-mono">
                                            Authorization: Bearer YOUR_API_KEY
                                        </code>
                                    </div>
                                </div>

                                {/* Get Coin Rewards */}
                                <div>
                                    <h4 className="text-md font-semibold text-gray-800 mb-3">Get Coin Rewards</h4>
                                    <p className="text-sm text-gray-600 mb-3">
                                        Retrieve coin rewards information (replace {`{coin}`} with btc, eth, ltc, etc.):
                                    </p>
                                    <div className="bg-gray-800 rounded-lg p-4 relative">
                                        <button
                                            onClick={() => copyToClipboard(`curl -H "Authorization: Bearer YOUR_API_KEY" https://cashlab.ae/api/main/reward/btc`, 'curl', 'coin-rewards')}
                                            className="absolute top-2 right-2 p-2 text-gray-400 hover:text-white transition-colors duration-200"
                                            title="Copy curl command"
                                        >
                                            <FontAwesomeIcon
                                                icon={copiedCurlId === 'coin-rewards' ? faCheck : faCopy}
                                                className={`w-4 h-4 ${copiedCurlId === 'coin-rewards' ? 'text-green-400' : ''}`}
                                            />
                                        </button>
                                        <code className="text-green-400 text-sm font-mono block">
                                            curl -H "Authorization: Bearer YOUR_API_KEY" \<br />
                                            &nbsp;&nbsp;https://cashlab.ae/api/main/reward/{`{coin}`}
                                        </code>
                                    </div>
                                    <div className="mt-4 bg-gray-100 rounded-lg p-4">
                                        <h5 className="text-sm font-semibold text-gray-700 mb-2">Response Format:</h5>
                                        <pre className="text-xs text-gray-600 overflow-x-auto">
{`{
  "coin": "btc",
  "totalValueInBtc": 0.00123456,
  "satoshiValue": 123456,
  "originalValue": 0.00123456,
  "originalUnit": "BTC",
  "dollarValue": 45.67
}`}
                                        </pre>
                                    </div>
                                </div>

                                {/* Admin Coin Rewards - Only show for admins */}
                                {isAdmin && (
                                    <div>
                                        <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                            Get Admin Coin Rewards
                                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                                                Admin Only
                                            </span>
                                        </h4>
                                        <p className="text-sm text-gray-600 mb-3">
                                            Retrieve detailed coin rewards information with admin privileges:
                                        </p>
                                        <div className="bg-gray-800 rounded-lg p-4 relative">
                                            <button
                                                onClick={() => copyToClipboard(`curl -H "Authorization: Bearer YOUR_API_KEY" https://cashlab.ae/api/main/admin/reward/btc`, 'curl', 'admin-rewards')}
                                                className="absolute top-2 right-2 p-2 text-gray-400 hover:text-white transition-colors duration-200"
                                                title="Copy curl command"
                                            >
                                                <FontAwesomeIcon
                                                    icon={copiedCurlId === 'admin-rewards' ? faCheck : faCopy}
                                                    className={`w-4 h-4 ${copiedCurlId === 'admin-rewards' ? 'text-green-400' : ''}`}
                                                />
                                            </button>
                                            <code className="text-green-400 text-sm font-mono block">
                                                curl -H "Authorization: Bearer YOUR_API_KEY" \<br />
                                                &nbsp;&nbsp;https://cashlab.ae/api/main/admin/reward/{`{coin}`}
                                            </code>
                                        </div>
                                        <div className="mt-4 bg-gray-100 rounded-lg p-4">
                                            <h5 className="text-sm font-semibold text-gray-700 mb-2">Response Format (Same as regular endpoint):</h5>
                                            <pre className="text-xs text-gray-600 overflow-x-auto">
{`{
  "coin": "btc",
  "totalValueInBtc": 0.00123456,
  "satoshiValue": 123456,
  "originalValue": 0.00123456,
  "originalUnit": "BTC",
  "dollarValue": 45.67
}`}
                                            </pre>
                                        </div>
                                    </div>
                                )}

                                {/* JavaScript Example */}
                                <div>
                                    <h4 className="text-md font-semibold text-gray-800 mb-3">JavaScript Example</h4>
                                    <p className="text-sm text-gray-600 mb-3">
                                        Using fetch() or axios to get coin rewards:
                                    </p>
                                    <div className="bg-gray-800 rounded-lg p-4 relative">
                                        <button
                                            onClick={() => copyToClipboard(`// Using fetch - Regular endpoint
fetch('https://cashlab.ae/api/main/reward/btc', {
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  }
})
.then(response => response.json())
.then(data => {
  console.log('Coin:', data.coin);
  console.log('Total Value in BTC:', data.totalValueInBtc);
  console.log('Satoshi Value:', data.satoshiValue);
  console.log('Original Value:', data.originalValue);
  console.log('Original Unit:', data.originalUnit);
  console.log('Dollar Value:', data.dollarValue);
});

// Using axios - Regular endpoint
axios.get('https://cashlab.ae/api/main/reward/btc', {
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY'
  }
})
.then(response => {
  const data = response.data;
  console.log('Reward data:', data);
});${isAdmin ? `

// Admin endpoint (requires admin privileges)
axios.get('https://cashlab.ae/api/main/admin/reward/btc', {
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY'
  }
})
.then(response => {
  const adminData = response.data;
  console.log('Admin reward data:', adminData);
});` : ''}`, 'curl', 'js-example')}
                                            className="absolute top-2 right-2 p-2 text-gray-400 hover:text-white transition-colors duration-200"
                                            title="Copy JavaScript code"
                                        >
                                            <FontAwesomeIcon
                                                icon={copiedCurlId === 'js-example' ? faCheck : faCopy}
                                                className={`w-4 h-4 ${copiedCurlId === 'js-example' ? 'text-green-400' : ''}`}
                                            />
                                        </button>
                                        <pre className="text-green-400 text-sm font-mono overflow-x-auto">
{`// Using fetch - Regular endpoint
fetch('https://cashlab.ae/api/main/reward/btc', {
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  }
})
.then(response => response.json())
.then(data => {
  console.log('Coin:', data.coin);
  console.log('Total Value in BTC:', data.totalValueInBtc);
  console.log('Satoshi Value:', data.satoshiValue);
  console.log('Original Value:', data.originalValue);
  console.log('Original Unit:', data.originalUnit);
  console.log('Dollar Value:', data.dollarValue);
});

// Using axios - Regular endpoint
axios.get('https://cashlab.ae/api/main/reward/btc', {
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY'
  }
})
.then(response => {
  const data = response.data;
  console.log('Reward data:', data);
});${isAdmin ? `

// Admin endpoint (requires admin privileges)
axios.get('https://cashlab.ae/api/main/admin/reward/btc', {
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY'
  }
})
.then(response => {
  const adminData = response.data;
  console.log('Admin reward data:', adminData);
});` : ''}`}
                                        </pre>
                                    </div>
                                </div>

                                {/* Python Example */}
                                <div>
                                    <h4 className="text-md font-semibold text-gray-800 mb-3">Python Example</h4>
                                    <p className="text-sm text-gray-600 mb-3">
                                        Using requests library to get coin rewards:
                                    </p>
                                    <div className="bg-gray-800 rounded-lg p-4 relative">
                                        <button
                                            onClick={() => copyToClipboard(`import requests

headers = {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
}

# Get regular coin rewards
response = requests.get('https://cashlab.ae/api/main/reward/btc', headers=headers)
if response.status_code == 200:
    data = response.json()
    print(f"Coin: {data['coin']}")
    print(f"Total Value in BTC: {data['totalValueInBtc']}")
    print(f"Satoshi Value: {data['satoshiValue']}")
    print(f"Original Value: {data['originalValue']}")
    print(f"Original Unit: {data['originalUnit']}")
    print(f"Dollar Value: {data['dollarValue']}")
else:
    print(f"Error: {response.status_code}")${isAdmin ? `

# Get admin coin rewards (requires admin privileges)
admin_response = requests.get('https://cashlab.ae/api/main/admin/reward/btc', headers=headers)
if admin_response.status_code == 200:
    admin_data = admin_response.json()
    print("Admin reward data:", admin_data)
else:
    print(f"Admin endpoint error: {admin_response.status_code}")` : ''}`, 'curl', 'python-example')}
                                            className="absolute top-2 right-2 p-2 text-gray-400 hover:text-white transition-colors duration-200"
                                            title="Copy Python code"
                                        >
                                            <FontAwesomeIcon
                                                icon={copiedCurlId === 'python-example' ? faCheck : faCopy}
                                                className={`w-4 h-4 ${copiedCurlId === 'python-example' ? 'text-green-400' : ''}`}
                                            />
                                        </button>
                                        <pre className="text-green-400 text-sm font-mono">
{`import requests

headers = {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
}

# Get regular coin rewards
response = requests.get('https://cashlab.ae/api/main/reward/btc', headers=headers)
if response.status_code == 200:
    data = response.json()
    print(f"Coin: {data['coin']}")
    print(f"Total Value in BTC: {data['totalValueInBtc']}")
    print(f"Satoshi Value: {data['satoshiValue']}")
    print(f"Original Value: {data['originalValue']}")
    print(f"Original Unit: {data['originalUnit']}")
    print(f"Dollar Value: {data['dollarValue']}")
else:
    print(f"Error: {response.status_code}")${isAdmin ? `

# Get admin coin rewards (requires admin privileges)
admin_response = requests.get('https://cashlab.ae/api/main/admin/reward/btc', headers=headers)
if admin_response.status_code == 200:
    admin_data = admin_response.json()
    print("Admin reward data:", admin_data)
else:
    print(f"Admin endpoint error: {admin_response.status_code}")` : ''}`}
                                        </pre>
                                    </div>
                                </div>

                                {/* Important Notes */}
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                    <h4 className="text-md font-semibold text-yellow-800 mb-2">Important Notes</h4>
                                    <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
                                        <li>Replace "YOUR_API_KEY" with your actual API key</li>
                                        <li>Replace "{`{coin}`}" with the actual coin symbol (btc, eth, ltc, etc.)</li>
                                        <li>Keep your API keys secure and never share them publicly</li>
                                        <li>API keys should be stored as environment variables in production</li>
                                        <li>All API requests must be made over HTTPS</li>
                                        {isAdmin && <li>Admin endpoints require admin privileges and will return 403 Forbidden for non-admin users</li>}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Confirmation Dialog */}
            {showConfirmDialog && keyToDeactivate && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                        <div className="p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Deactivation</h3>
                            <p className="text-gray-600 mb-6">
                                Are you sure you want to deactivate the API key "{keyToDeactivate.label}"?
                                This action cannot be undone and the key will no longer work.
                            </p>
                            <div className="flex gap-3 justify-end">
                                <button
                                    onClick={() => {
                                        setShowConfirmDialog(false);
                                        setKeyToDeactivate(null);
                                    }}
                                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors duration-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDeactivation}
                                    disabled={deactivatingId !== null}
                                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {deactivatingId !== null ? (
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    ) : (
                                        <FontAwesomeIcon icon={faTrash} />
                                    )}
                                    Deactivate
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ApiKeys;
